import {getShoppingSubscriptionResultInfo} from '@web/actions/result'
import {APIStatus} from '@web/core/constants'
import {reduceWithNullableNumberField} from '@web/helpers/policies'
import {
    createInitialPurchaseRewardPoint,
    createInitialReviewRewardPoint,
    RewardTypeKeys,
    getInitialState,
} from '@web/slices/reward/slice'
import makeSlice from '@web/slices/reward/sliceMaker'

import {fetchExpectedRewardPoint, fetchSubscription} from '../../actions'
import {FetchExpectedRewardPointPayload} from '../../actions/subscription/interfaces'

const refinePurchaseRewardPoint = (
    purchaseRewardPointsByItemId: Record<
        string,
        FetchExpectedRewardPointPayload['purchaseRewardPointsByItemId'][string]
    >,
    logeyeRewardPointListsByItemId: Record<
        string,
        FetchExpectedRewardPointPayload['logeyeRewardPointListsByItemId'][string]
    >,
) => {
    const purchaseRewardPoint = createInitialPurchaseRewardPoint()
    Object.entries(purchaseRewardPointsByItemId).forEach(([itemId, purchaseRewardPointByItemId]) => {
        purchaseRewardPoint.pointsByOrderSheetItemId[itemId] = {
            purchase: purchaseRewardPointByItemId.admin,
            merchant: purchaseRewardPointByItemId.seller,
            sellerCustomerManagementByTitle: purchaseRewardPointByItemId.sellerCustomerManagementByTitle ?? {},
            logeyeRewardPointByTitle: {},
            adminAdditional: purchaseRewardPointByItemId.adminAdditional,
            power: purchaseRewardPointByItemId.power,
            chargePoint: purchaseRewardPointByItemId.chargePoint,
            naverBankbook: purchaseRewardPointByItemId.naverBankbook,
            membership: purchaseRewardPointByItemId.membership ?? 0,
            arrivalGuarantee: purchaseRewardPointByItemId.arrivalGuarantee ?? 0,
            arrivalGuaranteeMembership: purchaseRewardPointByItemId.arrivalGuaranteeMembership ?? 0,
            expectedNaverBankbook: 0,
            expectedChargePoint: 0,
            totalRewardPoint: 0,
        }
        if (logeyeRewardPointListsByItemId && itemId in logeyeRewardPointListsByItemId) {
            const logeyeRewardPointList = logeyeRewardPointListsByItemId[itemId]
            logeyeRewardPointList?.forEach(({policyName, rewardPointAmount, membershipReward}) => {
                if (purchaseRewardPoint.pointsByOrderSheetItemId[itemId]) {
                    purchaseRewardPoint.pointsByOrderSheetItemId[itemId].logeyeRewardPointByTitle[policyName] = {
                        rewardAmount: rewardPointAmount,
                        membershipReward,
                    }
                }
            })
        }
    })

    return purchaseRewardPoint
}

const refineReviewRewardPoint = (
    reviewRewardPointsByItemId: Record<string, FetchExpectedRewardPointPayload['reviewRewardPointsByItemId'][string]>,
) => {
    const reviewRewardPoint = createInitialReviewRewardPoint()
    for (const key in reviewRewardPointsByItemId) {
        const reviewRewardPointByItemId = reviewRewardPointsByItemId[key]
        reviewRewardPoint.pointsByOrderSheetItemId[key] = {
            afterUsePhotoVideoReview: reviewRewardPointByItemId?.afterUsePhotoVideoReview ?? 0,
            afterUseTextReview: reviewRewardPointByItemId?.afterUseTextReview ?? 0,
            writePhotoVideoReview: reviewRewardPointByItemId?.writePhotoVideoReview ?? 0,
            writeTextReview: reviewRewardPointByItemId?.writeTextReview ?? 0,
            subscriberReview: reviewRewardPointByItemId?.subscriberReview ?? 0,
            maxRewardPoint: reviewRewardPointByItemId?.maxRewardPoint ?? 0,
            totalRewardPoint: 0,
        }
    }

    return reviewRewardPoint
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(getShoppingSubscriptionResultInfo.fulfilled, (state, {payload}) => {
                const {expectedRewardPoint} = payload.subscriptionResultViewInfo
                const {
                    expectedChargePoint,
                    expectedNaverBankbook,
                    cardEasyPayRewardPoint,
                    purchaseRewardPointsByItemId,
                    logeyeRewardPointListsByItemId,
                    reviewRewardPointsByItemId,
                } = expectedRewardPoint

                /** 구매적립 포인트 합산 */
                const purchaseRewardPoint = reduceWithNullableNumberField(
                    Object.values(purchaseRewardPointsByItemId).map((pointInfo) => {
                        const {sellerCustomerManagementByTitle, ...points} = pointInfo
                        /** 판매자 관리 추가 적립 포인트 */
                        const sellerRewardPoint = sellerCustomerManagementByTitle
                            ? reduceWithNullableNumberField(Object.values(sellerCustomerManagementByTitle))
                            : 0

                        return reduceWithNullableNumberField(Object.values(points)) + sellerRewardPoint
                    }),
                )

                /** 로그아이 적립포인트 합산 */
                const logeyeRewardPoint = reduceWithNullableNumberField(
                    Object.values(logeyeRewardPointListsByItemId).flatMap((pointList) =>
                        pointList.map(({rewardPointAmount}) => rewardPointAmount),
                    ),
                )

                /** 리뷰 적립 포인트 합산 */
                const reviewRewardPoint = reduceWithNullableNumberField(
                    Object.values(reviewRewardPointsByItemId).map(({maxRewardPoint}) => maxRewardPoint),
                )

                state.expectedChargePoint = expectedChargePoint
                state.expectedNaverBankbook = expectedNaverBankbook
                state.items = [
                    {
                        type: 'BENEFIT_CARD',
                        amount: cardEasyPayRewardPoint,
                        hasMembershipBenefit: false,
                    },
                    {type: 'PURCHASE', amount: purchaseRewardPoint, hasMembershipBenefit: false},
                    {type: 'REVIEW', amount: reviewRewardPoint, hasMembershipBenefit: false},
                    {
                        type: 'EXPECTED_MEMBERSHIP',
                        amount: logeyeRewardPoint,
                        hasMembershipBenefit: false,
                    },
                ]
                state.apiStatus = {
                    BENEFIT_CARD: APIStatus.RESOLVED,
                    EASY_BOOKING: APIStatus.RESOLVED,
                    EXPECTED_MEMBERSHIP: APIStatus.RESOLVED,
                    REVIEW: APIStatus.RESOLVED,
                    PURCHASE: APIStatus.RESOLVED,
                    EVENT: APIStatus.RESOLVED,
                }
            })
            .addCase(fetchSubscription.fulfilled, (state, {payload: {subscriptionViewResult}}) => {
                const {products} = subscriptionViewResult

                state.products = products.map((product) => {
                    return {
                        itemId: product.items[0]?.orderSheetItemId ?? '',
                        imageUrl: product.productImageUrl,
                        name: product.productName,
                    }
                })
            })
            .addCase(fetchExpectedRewardPoint.pending, (state) => {
                RewardTypeKeys.forEach((key) => {
                    state.apiStatus[key] = APIStatus.PENDING
                })
            })
            .addCase(fetchExpectedRewardPoint.fulfilled, (state, {payload}) => {
                const {logeyeRewardPointListsByItemId, purchaseRewardPointsByItemId, reviewRewardPointsByItemId} =
                    payload

                RewardTypeKeys.forEach((key) => {
                    state.apiStatus[key] = APIStatus.RESOLVED
                })

                state.purchaseRewardPoint = refinePurchaseRewardPoint(
                    purchaseRewardPointsByItemId,
                    logeyeRewardPointListsByItemId,
                )
                state.reviewRewardPoint = refineReviewRewardPoint(reviewRewardPointsByItemId)
            })
            .addCase(fetchExpectedRewardPoint.rejected, (state) => {
                Object.assign(state, getInitialState())

                RewardTypeKeys.forEach((key) => {
                    state.apiStatus[key] = APIStatus.REJECTED
                })
            })
    },
})

export default slice.reducer
export const actions = slice.actions
